/**
 * Tiptap/ProseMirror schema for yaqadocs.
 *
 * The document model stays a flat list of paragraph/heading blocks (matching
 * QdaDocument.paragraphs — see .claude/prosemirror-migration.md §3.2). Each
 * block carries a stable `paraId` attribute rendered as `data-para-id`, which
 * is the identity the yaqatw plugin uses to address text on the wire.
 *
 * Code blocks and hard breaks can't be expressed in the flat model and are
 * disabled so typing/markdown shortcuts can't produce content the plain-text
 * contract can't represent.
 *
 * Tables, and now bullet/ordered lists and blockquotes, are the deliberate
 * exceptions: they can't be flattened into the plain-text `paragraphs`
 * projection without loss, so the persisted document carries a
 * ProseMirror-JSON sidecar (`QdaDocument.content`) that round-trips them (see
 * persist.ts / convert.ts). None of these container nodes (table, bulletList,
 * orderedList, listItem, blockquote) get a `paraId` — the yaqatw plugin
 * addresses text by paragraphId/offset, and none of these has a single
 * top-level paragraph text range, so positionMap correctly refuses to map
 * positions inside them (highlights can't attach inside a table cell, list
 * item, or blockquote). In the flat projection each one collapses to one
 * plain-text paragraph / block index. This is automatic, not special-cased:
 * `pmDocToParagraphs`/`positionMap.ts` are generic over node type and only
 * look at top-level (depth-1) `paraId`, so any container node left without
 * that attribute gets this behavior for free.
 */

import { Extension, getSchema, AnyExtension } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Paragraph from "@tiptap/extension-paragraph";
import Heading from "@tiptap/extension-heading";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import Link from "@tiptap/extension-link";
import TextStyle from "@tiptap/extension-text-style";
import FontFamily from "@tiptap/extension-font-family";
import { FontSize } from "./fontSize";
import { LineSpacing } from "./lineSpacing";
import { IndentLevel } from "./indentLevel";
import { BlockDefaultFont } from "./blockFont";
import { ImageBlock, ImageInline } from "./imageNode";
import { CitationInline, CitationBlock } from "./citationNode";
import { ArtifactImage } from "./artifactImageNode";
import { Checklist, ChecklistItem } from "./checklistNode";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { ySyncPluginKey } from "y-prosemirror";
import { newId } from "../utils/idGenerator";

const paraIdAttribute = {
  paraId: {
    default: null as string | null,
    parseHTML: (el: HTMLElement) => el.getAttribute("data-para-id"),
    renderHTML: (attrs: Record<string, any>) =>
      attrs.paraId ? { "data-para-id": attrs.paraId } : {},
  },
};

export const ParagraphWithId = Paragraph.extend({
  addAttributes() {
    return { ...this.parent?.(), ...paraIdAttribute };
  },
});

export const HeadingWithId = Heading.extend({
  addAttributes() {
    return { ...this.parent?.(), ...paraIdAttribute };
  },
});

const controlAttributes = {
  controlId: {
    default: null as string | null,
    parseHTML: (el: HTMLElement) => el.getAttribute("data-control-id"),
    renderHTML: (attrs: Record<string, any>) => (attrs.controlId ? { "data-control-id": attrs.controlId } : {}),
  },
  title: {
    default: null as string | null,
    parseHTML: (el: HTMLElement) => el.getAttribute("data-control-title"),
    renderHTML: (attrs: Record<string, any>) => (attrs.title ? { "data-control-title": attrs.title } : {}),
  },
};

/**
 * A table wrapped in a "yaqat-cite-tag" content control when inserted as a
 * linked citation (see hostBridge.ts's insertTableAtCursor) — a table is
 * already block-level, so this extends the stock node with controlId/title
 * attrs directly rather than introducing a separate wrapper node.
 */
export const TableWithControl = Table.extend({
  addAttributes() {
    return { ...this.parent?.(), ...controlAttributes };
  },
});

/**
 * Keeps `paraId` present and unique across edits: splitting a paragraph copies
 * attrs to both halves, and newly typed paragraphs start with paraId null.
 * The first occurrence keeps its id (so existing highlights stay attached);
 * later duplicates and nulls get fresh ids.
 */
export const ParaIdGuard = Extension.create({
  name: "paraIdGuard",
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("paraIdGuard"),
        appendTransaction(transactions, _oldState, newState) {
          if (!transactions.some((tr) => tr.docChanged)) return null;
          // Don't reassign paraIds for changes that arrived from a remote peer
          // over Yjs: the peer that created the block already minted its id,
          // which syncs as a node attribute. Re-minting here (newId() is
          // nondeterministic) would diverge the id every collaborator sees —
          // and paraId is the identity highlights and the yaqatw wire depend on.
          if (
            transactions.some(
              (tr) => (tr.getMeta(ySyncPluginKey) as { isChangeOrigin?: boolean } | undefined)?.isChangeOrigin
            )
          ) {
            return null;
          }
          const seen = new Set<string>();
          let tr: any = null;
          newState.doc.forEach((node, offset) => {
            if (!("paraId" in node.attrs)) return;
            const id = node.attrs.paraId;
            if (id && !seen.has(id)) {
              seen.add(id);
              return;
            }
            tr = tr ?? newState.tr;
            const fresh = newId();
            seen.add(fresh);
            tr.setNodeMarkup(offset, undefined, { ...node.attrs, paraId: fresh });
          });
          return tr;
        },
      }),
    ];
  },
});

// Base StarterKit config: paragraph/heading are replaced by the *WithId
// variants below; code blocks/hard breaks can't be expressed in the flat
// plain-text model and stay disabled. `history` is left at its default here;
// the live editor toggles it via getLiveExtensions (Yjs collaboration
// supplies its own undo manager).
const starterKitConfig = {
  paragraph: false,
  heading: false,
  codeBlock: false,
  horizontalRule: false,
  hardBreak: false,
} as const;

// Default indent step, used unless a caller overrides it (see getLiveExtensions).
// This module deliberately has no dependency on settings/settingsStore: it's
// foundational plumbing imported by nearly every editor/docx/collab test, and
// settingsStore transitively imports settings/registry.ts, whose
// require.context() call only works under webpack, not Jest. Settings >
// General > Indent step is instead read by Editor.tsx (the actual caller of
// getLiveExtensions) and passed in as a plain number.
const DEFAULT_INDENT_STEP_PX = 24;

/** Node/mark extensions shared by every extension list (no history plugin). */
function buildNodeAndMarkExtensions(indentStep: number): AnyExtension[] {
  return [
    ParagraphWithId,
    HeadingWithId.configure({ levels: [1, 2, 3] }),
    Underline,
    Link.configure({ openOnClick: false }),
    TextStyle,
    FontFamily,
    FontSize,
    // Per-block text alignment. Renders as `style="text-align:…"` on the block
    // and lives as a `textAlign` node attribute — it survives save/reload via the
    // QdaDocument.content sidecar (the flat `paragraphs` projection drops it, like
    // inline marks). `left` is the default and renders no attribute.
    TextAlign.configure({ types: ["paragraph", "heading"] }),
    LineSpacing.configure({ types: ["paragraph", "heading"] }),
    IndentLevel.configure({ types: ["paragraph", "heading"], step: indentStep }),
    BlockDefaultFont.configure({ types: ["paragraph", "heading"] }),
    // Tables (prosemirror-tables under the hood). `resizable` enables the
    // column-resize handles; the accompanying CSS lives in index.css.
    TableWithControl.configure({ resizable: true }),
    TableRow,
    TableHeader,
    TableCell,
    // Image atoms (block + inline). Store only an assetId; bytes live in the
    // encrypted per-document asset store (assets.ts) and render via a NodeView.
    ImageBlock,
    ImageInline,
    // Citations + artifact images (Writing mode — see controlRegistry.ts and
    // hostBridge.ts's insertCitationAtCursor/insertArtifactImageAtCursor).
    CitationInline,
    CitationBlock,
    ArtifactImage,
    // Interactive checklist / to-do (Insert > Checklist). A container node with
    // no paraId, so it collapses to one plain-text block in the flat projection
    // (like a table) and highlights can't attach inside — see checklistNode.ts.
    Checklist,
    ChecklistItem,
    ParaIdGuard,
  ];
}

/**
 * Extensions shared by headless schema consumers (tests, convert,
 * getEditorSchema). Deliberately built with the fixed default step, not the
 * live setting — see indentStepPx's comment. These consumers only care about
 * indentLevel's schema *presence*, never its px math, so a fixed value here
 * is harmless; getLiveExtensions (the real editor) reads the live setting.
 */
export const baseExtensions: AnyExtension[] = [
  StarterKit.configure(starterKitConfig),
  ...buildNodeAndMarkExtensions(DEFAULT_INDENT_STEP_PX),
];

/**
 * The live editor's extension list. When `collaboration` is true the built-in
 * ProseMirror history plugin is disabled — the Yjs Collaboration extension owns
 * undo/redo via its UndoManager, and running both would double-apply. The schema
 * (nodes/marks) is identical either way, so positionMap/convert stay valid.
 *
 * `indentStepPx` is the caller's job to resolve (Editor.tsx reads Settings >
 * General > Indent step) — see this file's header comment on why schema.ts
 * itself must not import settingsStore.
 */
export function getLiveExtensions(collaboration: boolean, indentStepPx: number = DEFAULT_INDENT_STEP_PX): AnyExtension[] {
  return [
    StarterKit.configure({
      ...starterKitConfig,
      ...(collaboration ? { history: false } : {}),
    }),
    ...buildNodeAndMarkExtensions(indentStepPx),
  ];
}

export function getEditorSchema() {
  return getSchema(baseExtensions);
}
